import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import Button from '@mui/material/Button';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useState } from 'react';
import useMountedState from 'react-use/lib/useMountedState';
import { getRandomHumanAvatarProperties } from '../avatars/avatarEditor/avatarRandomUtils';
import { AVATAR_FULL_TEXTURE_SIZE } from '../constants/avatarConstants';
import { DEFAULT_AVATAR_PROPERTIES_SET } from '../types/avatarTypes';
import { getServiceAvatarUrl } from '../utils/avatarServiceUtils';
import { fetchToImageBitmap, scaleImageBitmapToDataUrl } from '../utils/imageUtils';
import { range, uuid } from '../utils/utils';
import { BUTTON_CONTAINER_CSS } from './devToolsContents';
const NUM_AVATARS = 30;
const AVATAR_IMAGE_CONTAINER_CSS = css `
  width: 200px;
  height: 200px;
  overflow: hidden;
  position: relative;
  background-color: rgba(255, 255, 255, 0.2);
`;
const AVATAR_IMAGE_CSS = css `
  position: absolute;
  left: 100px;
  top: 100px;
  transform: translate(-50%, -50%);
`;
const IMAGE_WIDTH = 400;
const IMAGE_HEIGHT = 400;
function createPlayerAvatar(properties) {
    return {
        type: 'custom',
        uuid: uuid(),
        avatar: { set: 'human', properties }
    };
}
function createRandomAvatar() {
    return createPlayerAvatar(getRandomHumanAvatarProperties());
}
function createDefaultAvatar() {
    return createPlayerAvatar(cloneDeep(DEFAULT_AVATAR_PROPERTIES_SET.properties));
}
export const DevToolsRandomAvatarsPage = () => {
    const [dataUrls, setDataUrls] = useState([]);
    const [avatarWidth, setAvatarWidth] = useState(512);
    const [avatarHeight, setAvatarHeight] = useState(512);
    const isMounted = useMountedState();
    const onClickMakeRandomAvatars = useCallback(async () => {
        const startTimestampMs = performance.now();
        const newAvatarResultPromises = range(NUM_AVATARS).map(() => fetchToImageBitmap(getServiceAvatarUrl({
            avatar: createRandomAvatar(),
            type: 'avatar',
            size: AVATAR_FULL_TEXTURE_SIZE,
            trimTransparentPixels: false
        })));
        // create a default avatar at the start of the list:
        newAvatarResultPromises.unshift(fetchToImageBitmap(getServiceAvatarUrl({
            avatar: createDefaultAvatar(),
            type: 'avatar',
            size: AVATAR_FULL_TEXTURE_SIZE,
            trimTransparentPixels: false
        })));
        const newDataUrls = (await Promise.all(newAvatarResultPromises)).map((avatarImageBitmap) => avatarImageBitmap && scaleImageBitmapToDataUrl(avatarImageBitmap, avatarWidth, avatarHeight));
        if (!isMounted()) {
            return;
        }
        setDataUrls(newDataUrls);
        const totalDurationMs = performance.now() - startTimestampMs;
        console.log(`Total time to generate ${NUM_AVATARS} avatars: ${totalDurationMs.toFixed(2)}ms (avg: ${(totalDurationMs / NUM_AVATARS).toFixed(2)}ms)`);
    }, [avatarHeight, avatarWidth, isMounted]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { css: BUTTON_CONTAINER_CSS, children: [_jsx(Button, { onClick: onClickMakeRandomAvatars, variant: 'contained', children: "Make Random Avatars" }), _jsx("input", { type: 'number', value: avatarWidth, onChange: (e) => {
                            setAvatarWidth(Number(e.target.value));
                        } }), ' ', "x", ' ', _jsx("input", { type: 'number', value: avatarHeight, onChange: (e) => {
                            setAvatarHeight(Number(e.target.value));
                        } })] }), _jsx("div", { css: css `
          display: flex;
          flex-flow: row wrap;
          gap: 5px;
        `, children: dataUrls.map((dataUrl, i) => {
                    if (!dataUrl) {
                        return null;
                    }
                    return (_jsx("div", { css: AVATAR_IMAGE_CONTAINER_CSS, children: _jsx("img", { src: dataUrl, css: AVATAR_IMAGE_CSS, width: IMAGE_WIDTH, height: IMAGE_HEIGHT }) }, i));
                }) })] }));
};
